import { useFieldState } from 'informed';
import { parsePhoneNumber } from 'libphonenumber-js';
import React, { FC, useMemo } from 'react';
import styled from 'styled-components';

import { Input } from './Input';
import { Select } from './Select';
import { countriesWithPhoneNumber } from '@/helpers/countriesWithPhoneNumber';
import { ThemeMode } from '@/helpers/enums';
import { maskPhone } from '@/helpers/masks';
import { required, validatePhone } from '@/helpers/validators';

import { device } from '@/styles/helpers';

interface InputPhoneNumberProps {
  name: string;
  initialValue?: string;
  initialCodeValue?: string;
  validateOnBlur?: boolean;
  withValidate?: boolean;
  disabled?: boolean;
  error?: string;
  onBlur?: () => void;
}

export const InputPhoneNumber: FC<InputPhoneNumberProps> = ({
  name,
  initialValue,
  initialCodeValue,
  disabled,
  ...props
}) => {
  const fieldCodeState = useFieldState<string | undefined>(`${name}.code`);

  const countriesOptions = countriesWithPhoneNumber.map(
    ({ name, flagUrl, dialCode, iso2 }) => ({
      icon: flagUrl,
      name: `+${dialCode} | ${name}`,
      nameS: `+${dialCode} | ${name}`,
      nameV: `+${dialCode}`,
      value: iso2,
    })
  );

  const {
    country: initialValueCountry,
    nationalNumber: initialValueNationalNumber,
  } = useMemo(() => {
    const isFullNumber =
      typeof initialValue === 'string' && initialValue?.includes('+');

    if ((!initialValue || !isFullNumber) && !!initialCodeValue) {
      return {
        country: initialCodeValue,
        nationalNumber: initialValue,
      };
    }

    if (!initialValue || !isFullNumber)
      return {
        country: undefined,
        nationalNumber: initialValue,
      };

    return parsePhoneNumber(initialValue);
  }, [initialValue, initialCodeValue]);

  const currentCountry = useMemo(() => {
    if (!fieldCodeState || !fieldCodeState.value) return initialValueCountry;

    return fieldCodeState.value;
  }, [fieldCodeState, initialValueCountry]);

  return (
    <ScInputPhoneNumber>
      <Select
        initialValue={initialValueCountry}
        disabled={disabled}
        placeholder="Prefix"
        name={`${name}.code`}
        options={countriesOptions}
        validate={required}
        search
        cols={1}
        mode={ThemeMode.Light}
        scrollBlockTopMargin={0}
      />
      <Input
        {...props}
        initialValue={initialValueNationalNumber as string}
        disabled={disabled}
        key={currentCountry}
        name={`${name}.number`}
        placeholder="Number"
        parser={(v) => {
          return maskPhone(v?.toString(), currentCountry);
        }}
        validate={(v) => {
          return validatePhone(v, currentCountry);
        }}
      />
    </ScInputPhoneNumber>
  );
};

const ScInputPhoneNumber = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 160px auto;
  grid-column-gap: 8px;
  grid-row-gap: 8px;

  @media (${device.mobile}) {
    grid-template-columns: 130px auto;
  }
`;
